import { useEffect, useRef, useState } from "react";
import { FileText, Loader2, Paperclip, Trash2, Upload, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  INVOICE_ACCEPT_ATTR,
  MAX_INVOICE_MB,
  deleteInvoice,
  formatInvoiceDate,
  formatInvoiceSize,
  listComponentInvoices,
  uploadInvoice,
  validateInvoiceFile,
} from "@/lib/invoices";
import { toast } from "sonner";

/**
 * Invoice section for the component dialog.
 *
 * With a componentId (edit mode) files are uploaded immediately and the existing
 * invoices are listed. Without one (create mode) the chosen files are held in the
 * parent's `stagedFiles` and uploaded by the caller once the component exists.
 */
export default function ComponentInvoiceField({
  componentId,
  stagedFiles = [],
  onStagedFilesChange,
  onChanged,
  readOnly = false,
}) {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const staged = !componentId;

  useEffect(() => {
    if (componentId) {
      fetchExisting();
    } else {
      setInvoices([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [componentId]);

  const fetchExisting = async () => {
    setLoading(true);
    try {
      setInvoices(await listComponentInvoices(componentId));
    } catch (error) {
      console.error("Error fetching invoices:", error);
      toast.error("Failed to load invoices");
    } finally {
      setLoading(false);
    }
  };

  const addStaged = (files) => {
    const accepted = [];
    for (const file of files) {
      if (!file) continue;
      const problem = validateInvoiceFile(file);
      if (problem) {
        toast.error(`${file.name}: ${problem}`);
        continue;
      }
      accepted.push(file);
    }
    if (accepted.length) {
      onStagedFilesChange?.([...stagedFiles, ...accepted]);
    }
  };

  const uploadNow = async (files) => {
    setUploading(true);
    let uploaded = 0;
    for (const file of files) {
      const problem = validateInvoiceFile(file);
      if (problem) {
        toast.error(`${file.name}: ${problem}`);
        continue;
      }
      try {
        await uploadInvoice(componentId, file);
        uploaded += 1;
      } catch (error) {
        console.error("Error uploading invoice:", error);
        toast.error(
          `${file.name}: ${error.response?.data?.detail || "upload failed"}`
        );
      }
    }
    setUploading(false);
    if (uploaded) {
      toast.success(uploaded === 1 ? "Invoice uploaded" : `${uploaded} invoices uploaded`);
      await fetchExisting();
      onChanged?.();
    }
  };

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    if (staged) {
      addStaged(files);
    } else {
      await uploadNow(files);
    }
    if (inputRef.current) inputRef.current.value = "";
  };

  const removeStaged = (index) => {
    onStagedFilesChange?.(stagedFiles.filter((_, i) => i !== index));
  };

  const removeInvoice = async (invoice) => {
    try {
      await deleteInvoice(invoice.id);
      toast.success("Invoice deleted");
      await fetchExisting();
      onChanged?.();
    } catch (error) {
      console.error("Error deleting invoice:", error);
      toast.error("Failed to delete invoice");
    }
  };

  return (
    <div className="space-y-3">
      {!readOnly && (
        <>
          <Input
            ref={inputRef}
            type="file"
            multiple
            accept={INVOICE_ACCEPT_ATTR}
            className="rounded-none"
            onChange={(e) => handleFiles(e.target.files)}
            disabled={uploading}
            data-testid="component-invoice-input"
          />
          <p className="text-xs text-muted-foreground">
            PDF, JPG or PNG — up to {MAX_INVOICE_MB} MB each, several allowed.
            {staged
              ? " Files are attached as soon as you save the component."
              : ""}
          </p>
        </>
      )}

      {uploading && (
        <p className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="w-3 h-3 animate-spin" /> Uploading…
        </p>
      )}

      {/* Files picked while creating the component */}
      {staged && stagedFiles.length > 0 && (
        <ul className="divide-y divide-border border border-border">
          {stagedFiles.map((file, index) => (
            <li key={`${file.name}-${index}`} className="flex items-center gap-2 px-3 py-2">
              <Paperclip className="w-3 h-3 shrink-0 text-muted-foreground" />
              <span className="flex-1 text-sm break-all">{file.name}</span>
              <span className="text-xs text-muted-foreground whitespace-nowrap">
                {formatInvoiceSize(file.size)}
              </span>
              {!readOnly && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  title="Remove"
                  onClick={() => removeStaged(index)}
                >
                  <X className="w-3 h-3" />
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}

      {/* Invoices already stored on the component */}
      {!staged && (
        <div className="border border-border">
          {loading ? (
            <p className="px-3 py-2 text-xs text-muted-foreground">Loading invoices…</p>
          ) : invoices.length === 0 ? (
            <p className="px-3 py-2 text-xs text-muted-foreground">
              No invoice attached yet.
            </p>
          ) : (
            <ul className="divide-y divide-border">
              {invoices.map((invoice) => (
                <li key={invoice.id} className="flex items-center gap-2 px-3 py-2">
                  <FileText className="w-3 h-3 shrink-0 text-muted-foreground" />
                  <span className="flex-1 text-sm break-all">
                    {invoice.filename}
                    {invoice.notes ? (
                      <span className="block text-xs text-muted-foreground">
                        {invoice.notes}
                      </span>
                    ) : null}
                  </span>
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    {formatInvoiceSize(invoice.size)} · {formatInvoiceDate(invoice.uploaded_at)}
                  </span>
                  {!readOnly && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      title="Delete"
                      onClick={() => removeInvoice(invoice)}
                      data-testid={`component-invoice-delete-${invoice.id}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {!staged && invoices.length > 0 && (
        <p className="text-xs text-muted-foreground">
          <Upload className="inline w-3 h-3 mr-1" />
          Use the paperclip in the components list for preview, notes and download.
        </p>
      )}
    </div>
  );
}
